import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { buildEvidenceUrl, buildEvidenceLabel } from '../domain/sourceRef';

// TimelineCard — typed renderer for build_timeline results.
//
// Dispatched by ChatMessage on `tool_trace[*].tool === 'build_timeline'`.
// Renders directly from the structured result envelope (graph-backed,
// already sorted dated-first then undated). The LLM's prose content
// becomes the narrative caption above the card; this component owns
// the structured artifact.
//
// Envelope contract (see _graph_helpers.build_timeline_from_graph and
// facades/analysis_facade.build_timeline):
//
//   {
//     schema_version: 1,
//     events: [
//       {
//         date: "2022-08-15" | "",       // ISO date or "" for undated
//         event: "Briefing at ...",
//         event_type: "discrete_event" | "...",
//         participants: ["Jeanine Camp", "..."],
//         sources: [{file, document_id}],
//         source_chunk_ids: [...],
//         confidence_bucket: "HIGH" | "MEDIUM" | "LOW",
//         document_id: "...",            // primary source for the click
//         filename: "...",
//       },
//     ],
//     event_count: N,
//     ...
//   }
//
// scope_too_broad envelope is intentionally NOT handled here — the
// LLM's prose already explains the narrowing options.

function formatDateHeader(iso) {
  if (!iso) return 'Undated';
  // The graph path emits ISO from a TSTZRANGE lower bound; the legacy
  // path emits plain YYYY-MM-DD. Take the first 10 chars and pretty-
  // print without depending on the user's locale formatter.
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toLocaleDateString(undefined, {
    year: 'numeric', month: 'long', day: 'numeric',
  });
}

function groupByDate(events) {
  // Preserve the server's sort order (dated-first then undated) and
  // bucket consecutive same-date events. Going back to a Map keyed by
  // date would lose the upstream tie-break (event_id) on ties.
  const groups = [];
  let current = null;
  for (const ev of events) {
    const key = ev.date || '';
    if (!current || current.key !== key) {
      current = { key, label: formatDateHeader(key), items: [] };
      groups.push(current);
    }
    current.items.push(ev);
  }
  return groups;
}

function ConfidencePill({ bucket }) {
  if (!bucket) return null;
  const b = String(bucket).toLowerCase();
  const cls = `timeline-card__confidence timeline-card__confidence--${b}`;
  return <span className={cls} title={`Confidence: ${bucket}`}>{b}</span>;
}

function SourceLink({ ev, pathname }) {
  // Each event has a primary document_id + filename (the chunk it was
  // extracted from). Reuse the citation URL builder so click behavior
  // matches the rest of the chat — opens the viewer modal at the chunk.
  if (!ev.document_id) return null;
  const ref = {
    document_id: ev.document_id,
    filename: ev.filename || '',
    chunk_id: (ev.source_chunk_ids || [])[0],
    snippet: ev.event || '',
    kind: 'none',
    verified: true,
  };
  const href = buildEvidenceUrl(ref, pathname);
  const label = buildEvidenceLabel(ref);
  if (!href) return null;
  return (
    <a href={href} className="citation-link timeline-card__source">
      📄 {label}
    </a>
  );
}

export default function TimelineCard({ result }) {
  const { pathname } = useLocation();
  const events = useMemo(
    () => Array.isArray(result?.events) ? result.events : [],
    [result],
  );
  const groups = useMemo(() => groupByDate(events), [events]);

  // Defensive: scope_too_broad envelope is handled by the LLM's prose.
  // Render nothing here so we don't double-surface the narrowing flow.
  if (result?.error === 'scope_too_broad') return null;

  if (!events.length) {
    return (
      <div className="timeline-card timeline-card--empty">
        <div className="timeline-card__header">Timeline</div>
        <p className="timeline-card__empty-text">
          No events found for this scope.
        </p>
      </div>
    );
  }

  const datedCount = events.filter(e => e.date).length;

  return (
    <div className="timeline-card">
      <div className="timeline-card__header">
        <span className="timeline-card__title">Timeline</span>
        <span className="timeline-card__meta">
          {events.length} event{events.length === 1 ? '' : 's'}
          {datedCount < events.length && (
            <> · {datedCount} dated, {events.length - datedCount} undated</>
          )}
        </span>
      </div>

      <ol className="timeline-card__groups">
        {groups.map((g, gi) => (
          <li key={`${g.key}-${gi}`} className="timeline-card__group">
            <div className={`timeline-card__date ${g.key ? '' : 'timeline-card__date--undated'}`}>
              {g.label}
            </div>
            <ul className="timeline-card__events">
              {g.items.map((ev, ei) => (
                <li key={ev.event_id || `${gi}-${ei}`} className="timeline-card__event">
                  <div className="timeline-card__event-row">
                    <span className="timeline-card__event-text">{ev.event}</span>
                    <ConfidencePill bucket={ev.confidence_bucket} />
                  </div>
                  {ev.participants?.length > 0 && (
                    <div className="timeline-card__participants">
                      {ev.participants.join(' · ')}
                    </div>
                  )}
                  <SourceLink ev={ev} pathname={pathname} />
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ol>
    </div>
  );
}
